<script setup>
import { computed, defineProps, ref } from "vue";
import { useStore } from "vuex";
import { AppButton, SimpleSelect } from "@n11/so-ui-components";
import { FlexRender, getCoreRowModel, useVueTable } from "@tanstack/vue-table";
import Pagination from "@/components/Table/Pagination";
import { DEAL_LISTING_PAGE_SIZES } from "@/utils/enum/configs";

const store = useStore();
const pageConfig =
  store.getters["common/getConfigList"][DEAL_LISTING_PAGE_SIZES];

//config apisinden data gelmemesi durumu icin default pagesizes verildi
const defaultPageSizes = {
  10: "10 Ürün",
  25: "25 Ürün",
  50: "50 Ürün",
  100: "100 Ürün",
};

const emit = defineEmits(["goBulkDealManagement", "onPageChanged"]);

const props = defineProps({
  isLoading: {
    type: Boolean,
    required: true,
  },
  columns: {
    type: Array,
    required: true,
  },
  data: {
    type: Array,
    required: true,
  },
  pagination: {
    type: Object,
    required: true,
  },
  pageCount: {
    type: Number,
    required: true,
  },
  pageChanged: {
    type: Function,
    required: true,
  },
});

const tableDealRef = ref(null);

const visibleRows = computed(() => {
  return (table?.value.getRowModel().rows || []) ?? [];
});

const table = computed(() => {
  const { columns, data, pageCount, pagination } = props;
  return useVueTable({
    get data() {
      return data ?? [];
    },
    get pageCount() {
      return pageCount;
    },
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    onPaginationChange: (updater) => {
      if (typeof updater === "function") {
        setPagination(
          updater({
            pageIndex: props.pagination.page,
            pageSize: props.pagination.pageSize,
          })
        );
      } else {
        setPagination(updater);
      }
    },
    rowCount: data?.length ?? 0,
    state: {
      pagination,
    },
  });
});

const getHeaderWidthStyle = (header) => {
  const windowWidth = tableDealRef.value.offsetWidth;
  const percentage = Math.round((header.getSize() / windowWidth) * 100);
  return { width: `${percentage}%` };
};

const setPagination = ({ pageIndex, pageSize }) => {
  const updatedPagination = { ...props.pagination, page: pageIndex, pageSize };
  emit("onPageChanged", updatedPagination);
};

const clickHandler = (pageNum) => {
  table.value.setPageIndex(pageNum);
};

const getPageSizeFromConfig = () => {
  if (!pageConfig || typeof pageConfig !== "string") {
    return defaultPageSizes;
  }
  const pageConfigArray = pageConfig.split(";").filter(Boolean);
  const newPageSizes = {};

  pageConfigArray.forEach((size) => {
    newPageSizes[size] = `${size} Ürün`;
  });
  return { ...defaultPageSizes, ...newPageSizes };
};

const onSimpleSelectChange = (newSize) => {
  table.value.setPageSize(newSize);
};
</script>

<template>
  <div ref="tableDealRef" class="p-2 advanced-table-wrapper">
    <div v-if="isLoading" class="loading">
      <img src="../../../assets/images/loading.gif" alt="Loading" />
    </div>
    <div v-else-if="visibleRows.length === 0" class="empty-table">
      <img
        src="../../../assets/images/empty-search.png"
        alt="Ürün bulunamadı"
      />
      <span class="title"
        >Bu tarih aralığında komisyon teklifi bulunmuyor.</span
      >
    </div>
    <template v-else>
      <div class="table-top">
        <p class="table-top-text">
          Mağazanıza özel komisyon tekliflerinden dilediğinizi seçerek; düşük
          komisyon oranlarından faydalanın ve satışlarınızı artırın.
        </p>
        <AppButton
          secondary
          sm
          title="Toplu Teklif Yönetimine Git"
          class="table-top-btn"
          @click="emit('goBulkDealManagement')"
        >
          <template #prependIcon>
            <span class="table-top-btn-badge">YENİ</span>
          </template>
        </AppButton>
      </div>
      <div class="table-wrapper">
        <table class="advanced-table">
          <thead>
            <tr
              v-for="headerGroup in table.getHeaderGroups()"
              :key="headerGroup.id"
            >
              <th
                v-for="header in headerGroup.headers"
                :key="header.id"
                :colSpan="header.colSpan"
                :style="[getHeaderWidthStyle(header)]"
              >
                <FlexRender
                  v-if="!header.isPlaceholder"
                  :render="header.column.columnDef.header"
                  :props="header.getContext()"
                />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in visibleRows" :key="row.id">
              <td v-for="cell in row.getVisibleCells()" :key="cell.id">
                <FlexRender
                  :render="cell.column.columnDef.cell"
                  :props="cell.getContext()"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="pagination flex items-center gap-2">
        <Pagination
          :pageCount="table.getPageCount()"
          :pageRange="5"
          :clickHandler="clickHandler"
          :firstLastButton="true"
          containerClass="button-group"
          prevLinkClass="border left rounded p-1"
          pageLinkClass="border rounded p-1"
          nextLinkClass="border right rounded p-1"
          :marginPages="0"
          :value="table.getState().pagination.page"
        >
        </Pagination>
        <SimpleSelect
          :simpleSelectItems="getPageSizeFromConfig()"
          :selectedKey="table.getState().pagination.pageSize"
          openMenuPosition="top-left"
          @btnItemClick="onSimpleSelectChange(Number($event))"
        />
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/variables";
@import "@/assets/styles/utilities";

.advanced-table-wrapper {
  display: flex;
  gap: 16px;
  flex-direction: column;

  .table-top {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &-text {
      font-size: 14px;
      line-height: 20px;
      letter-spacing: -0.05px;
      color: $colorN80;
    }

    &-btn {
      max-width: fit-content;

      &-badge {
        border-radius: 3px;
        background-color: $colorL50;
        color: $colorN80;
        font-weight: 700;
        font-size: 10px;
        padding: 0 3px;
        margin-right: 8px;
        height: 18px;
      }
    }
  }

  .loading {
    height: 128px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .empty-table {
    gap: 24px;
    display: flex;
    padding: 64px 0;
    align-self: stretch;
    align-items: center;
    flex-direction: column;
    border-radius: 8px;
    border: 1px solid $colorN50;

    img {
      max-width: 120px;
    }

    .title {
      font-size: 16px;
      font-weight: 700;
      line-height: 20px;
      letter-spacing: -0.05px;
      color: $colorN80;
    }
  }
}

.table-wrapper {
  width: 100%;
  position: relative;
  border: 1px solid $colorN50;
  border-radius: 8px;
  border-spacing: 0;
  border-collapse: separate;
  padding: 2px;

  .empty-table {
    display: flex;
    padding: 64px 0;
    align-items: center;
    flex-direction: column;

    img {
      max-width: 120px;
      margin-bottom: 24px;
    }

    > span {
      display: block;
      font-size: 16px;
      line-height: 20px;
      letter-spacing: -0.05px;
      color: $colorN80;

      &:first-of-type {
        font-weight: 700;
        margin-bottom: 12px;
      }

      &:last-of-type {
        font-weight: 600;
        margin-bottom: 16px;
      }
    }

    button {
      width: 193px;
      gap: 4px;

      img {
        margin: auto 0;
      }
    }
  }
}

.actions-row {
  display: flex;
  width: 100%;
  justify-content: space-between;
}

.advanced-table {
  width: 100%;
  border-radius: 8px;
  border-collapse: separate;

  thead {
    background: $colorD10;
    color: $colorN80;

    tr {
      height: 50px;

      th {
        padding: 12px;
        text-align: left;
        vertical-align: middle;
        font-size: 13px;
        font-weight: 600;
        line-height: 18px;
        letter-spacing: -0.05px;
        color: $colorN80;
        background-color: $colorBe20;
        border-bottom: 2px solid $colorN50;

        &:not(:last-child) {
          border-right: 1px solid $colorN50;
        }
      }
    }
  }

  tbody {
    tr {
      td {
        background-color: $colorN00;
        height: 90px;
        vertical-align: middle;

        > {
          div {
            margin: auto;
          }

          label {
            display: block;
            margin: 0 8px;
          }

          .product-info {
            width: 256px;
            padding: 0 12px;
          }

          .popover-wrapper {
            padding: 0 16px;
          }

          .checkbox {
            display: flex;
            justify-content: center;
          }
        }

        &:not(:last-child) {
          border-right: 1px solid $colorN50;
        }
      }

      &.disabled {
        td {
          background-color: $colorD05;
        }
      }

      &:not(:last-child) {
        td {
          border-bottom: 2px solid $colorN50;
        }
      }

      &:last-child {
        td :deep(.tooltip-text) {
          top: 60px !important;
        }
      }
    }
  }
}

.pagination {
  display: flex;
  width: 100%;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
}

.buttons {
  display: flex;
  gap: 12px;
}
</style>
